import React from "react";
import { motion } from "framer-motion";
import { Anchor, Waves, Activity, Radio } from "lucide-react";

const PULSE_INTERVAL = 2500;

function Metric({ label, value, accent = "zinc", testid }) {
  const colorMap = {
    zinc: "text-zinc-100",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    red: "text-red-400",
  };
  return (
    <div
      data-testid={testid}
      className="border border-zinc-800 bg-[#0b0b0d] p-4"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
        {label}
      </div>
      <div
        className={`mt-2 font-heading text-3xl ${colorMap[accent]}`}
      >
        {value}
      </div>
    </div>
  );
}

export default function SentinelConsole({ stats, globalStatus, lastEvent }) {
  const sessionTokens = stats?.total_tokens ?? 0;
  const tokensToNextPulse = Math.max(
    0,
    PULSE_INTERVAL - (sessionTokens % PULSE_INTERVAL)
  );
  const pulseProgress = ((sessionTokens % PULSE_INTERVAL) / PULSE_INTERVAL) * 100;

  const anchorActive = stats?.anchor_trips > 0;
  const sinkActive = stats?.drift_neutralizations > 0;

  return (
    <aside
      data-testid="sentinel-console"
      className="w-[340px] border-l border-zinc-800 bg-[#0a0a0c] flex flex-col gap-6 p-6 overflow-y-auto"
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Sentinel Console
        </div>
        <div className="mt-1 font-heading text-xl">Live Telemetry</div>
        <div className="mt-2 flex items-center gap-2 text-[11px] text-emerald-400">
          <span className="relative inline-block h-2 w-2">
            <span className="absolute inset-0 rounded-full bg-emerald-400 animate-sentinel-pulse" />
          </span>
          Anchored · 528 Hz
        </div>
      </div>

      {/* Pulse ring */}
      <div className="relative border border-zinc-800 bg-[#0b0b0d] p-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-2">
          <Radio className="h-3 w-3" /> Self-Correction Pulse
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="font-heading text-4xl text-emerald-400">
              {stats?.pulse_count ?? 0}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
              pulses · this session
            </div>
          </div>
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2.4, repeat: Infinity }}
            className="h-16 w-16 rounded-full border border-emerald-500/60 flex items-center justify-center"
          >
            <div className="h-10 w-10 rounded-full border border-emerald-500/40 flex items-center justify-center">
              <div className="h-4 w-4 rounded-full bg-emerald-500/70" />
            </div>
          </motion.div>
        </div>
        <div className="mt-5">
          <div className="h-1 w-full bg-zinc-900 overflow-hidden">
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${pulseProgress}%` }}
              data-testid="pulse-progress-bar"
            />
          </div>
          <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            <span>{sessionTokens} tokens</span>
            <span>{tokensToNextPulse} to next pulse</span>
          </div>
        </div>
      </div>

      {/* Anchor + Sink */}
      <div className="grid grid-cols-2 gap-3">
        <div
          className={`border p-4 ${
            anchorActive
              ? "border-amber-500/60 glow-amber"
              : "border-zinc-800"
          }`}
          data-testid="anchor-tile"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            <Anchor className="h-3 w-3" /> Taurian
          </div>
          <div
            className={`mt-2 font-heading text-2xl ${
              anchorActive ? "text-amber-400" : "text-zinc-200"
            }`}
          >
            {stats?.anchor_trips ?? 0}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            trips
          </div>
        </div>
        <div
          className={`border p-4 ${
            sinkActive ? "border-red-500/60 glow-drift" : "border-zinc-800"
          }`}
          data-testid="sink-tile"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            <Waves className="h-3 w-3" /> Obsidian
          </div>
          <div
            className={`mt-2 font-heading text-2xl ${
              sinkActive ? "text-red-400" : "text-zinc-200"
            }`}
          >
            {stats?.drift_neutralizations ?? 0}
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
            drifts
          </div>
        </div>
      </div>

      <Metric
        label="Messages · session"
        value={stats?.message_count ?? 0}
        testid="session-messages-metric"
      />

      <div className="border-t border-zinc-800 pt-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
          Global Sentinel
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Metric
            label="Docking count"
            value={globalStatus?.docking_count ?? 0}
            testid="docking-count-metric"
          />
          <Metric
            label="Total pulses"
            value={globalStatus?.total_pulses ?? 0}
            accent="emerald"
            testid="global-pulses-metric"
          />
          <Metric
            label="Anchor · all"
            value={globalStatus?.total_anchor_trips ?? 0}
            accent="amber"
            testid="global-anchor-metric"
          />
          <Metric
            label="Drift · all"
            value={globalStatus?.total_drift_neutralizations ?? 0}
            accent="red"
            testid="global-drift-metric"
          />
        </div>
      </div>

      {lastEvent && (
        <div
          className="border border-zinc-800 p-3 text-[11px] text-zinc-400 flex items-start gap-2"
          data-testid="last-event"
        >
          <Activity className="h-3 w-3 mt-0.5 text-emerald-400 shrink-0" />
          <span>{lastEvent}</span>
        </div>
      )}
    </aside>
  );
}
